/* LinksRenderer.js */

"use strict";
const Registrations = require("../../../../../../../RegistrationNamespaces");
const MoveVeilComponent = require("./../../move_veil/MoveVeilComponent");
const _ = require("lodash");

class LinksRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("links-column");
        this._rootElem.component = this._model;

        this._planRemoveIcon = document.createElement("actionable-icon-gl");
        this._planRemoveIcon.dataset.icon = "common.trash_bin";
        this._planRemoveIcon.setAttribute("title", "Remove");

        this._updateStatusIcon = document.createElement("actionable-icon-gl");
        this._updateStatusIcon.dataset.icon = "osmanager.edit_os_plan_associations";
        this._updateStatusIcon.setAttribute("title", "Update Device Status");

        this.deviceIds = [];
        // veil component opened by this renderer (moveVeilLaunch); the only one _shutdown may close
        this._ownedVeilComponent = undefined;

        this._registerListeners();
        this.render();
    }

    /* *******************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS  */
    /* *******************************************/

    _shutdown() {
        let promiseAll = [];
        // Grid cells are torn down whenever the related-device feed drops a row, e.g. while
        // another view (Manage Devices) is saving behind its own veil. Only close a veil this
        // renderer opened itself (PE-11108 / PE-14640).
        if (this._ownedVeilComponent && this._isOwnedVeilDisplayed()) {
            promiseAll = [this._model._modalUserFeedback.hideDisplayed()];
        }
        this._ownedVeilComponent = undefined;
        return Promise.all(promiseAll).then(() => {
            this._unregisterListeners();
            this._planRemoveIcon.remove();
            this._planRemoveIcon = undefined;
            this._updateStatusIcon.remove();
            this._updateStatusIcon = undefined;
            this.deviceIds = undefined;
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            return Promise.resolve();
        });
    }

    _registerListeners() {
        this._planRemoveIcon.addEventListener(
            "click",
            (this._onClickPlanRemoveIcon = this._onClickPlanRemoveIcon.bind(this))
        );
        this._updateStatusIcon.addEventListener(
            "click",
            (this._onClickUpdateStatusIcon = this._onClickUpdateStatusIcon.bind(this))
        );
        this._model.on("Links", (this._onLinks = this._onLinks.bind(this)));
    }

    _unregisterListeners() {
        this._planRemoveIcon.removeEventListener("click", this._onClickPlanRemoveIcon);
        this._updateStatusIcon.removeEventListener("click", this._onClickUpdateStatusIcon);
        this._model.removeAllListeners("Links");
    }

    /** navigate inside view tab */
    _switchView(switchTo, params) {
        this._model._gameEditor.setActiveView(
            switchTo,
            [
                {
                    value: {
                        fqn: Registrations.OS_PLAN_EDITOR_VIEW_DETAILS,
                        viewState: {configExecutionEntity: this._model.getValue()},
                    },
                    content: "Details",
                    left: {
                        arrow: {
                            direction: "left",
                        },
                    },
                },
            ],
            params
                ? params
                : {
                      configExecutionEntity: this._model.getValue(),
                  }
        );
    }

    _onClickPlanRemoveIcon() {
        this._model.removeDevice();
    }

    _onClickUpdateStatusIcon() {
        this._model.updateDeviceStatus();
    }

    _isOwnedVeilDisplayed() {
        return (
            _.get(this._model, "_modalUserFeedback._displayed") === this._ownedVeilComponent &&
            this._model._modalUserFeedback.isStillOpen
        );
    }

    moveVeilLaunch() {
        let moveVeilComponent = new MoveVeilComponent(
            this._model._logger,
            this._model._gameEditor,
            this.deviceIds
        );
        this._ownedVeilComponent = moveVeilComponent;
        return this._model._modalUserFeedback
            .displayCustom(moveVeilComponent, {dontHide: true})
            .then((result) => {
                return this.moveToPlan(result.type, result.id);
            })
            .catch((err) => {
                if (err && err.userRejected) {
                    this._model._logger.warn(
                        {error: err},
                        "OsPlanDetails._onClickPlanMoveIcon() - User Canceled"
                    );
                    this._model._modalUserFeedback.hideDisplayed();
                    return;
                } else if (err.stack) {
                    this._model._modalUserFeedback.hideDisplayed();
                    // this._model._logger.warn(
                    //     { error: err },
                    //     "OsPlanDetails._onClickPlanMoveIcon() - Error"
                    // );
                    return;
                } else {
                    this._model._logger.warn(
                        {error: err},
                        "OsPlanDetails._onClickPlanMoveIcon() - Error"
                    );
                    return this._model._modalUserFeedback.displayError(err);
                }
            });
    }

    /* jshint ignore:start */

    async moveToPlan(moveType, planNameOrID) {
        try {
            const deviceIds = this.deviceIds;
            const groupKey = _.get(this._model._value, "groupBy", null)
                ? this._model._value.id
                : "";
            if (moveType === "new") {
                const newPlanId = await this._model._gameEditor.createPlan(planNameOrID.trim());
                await newPlanId.setAction(this._model._gameEditor.getPlan().action);
                await newPlanId.update();
                await this._model._gameEditor.moveDevices(newPlanId.id, deviceIds);
            } else {
                await this._model._gameEditor.moveDevices(planNameOrID, deviceIds, groupKey);
            }
        } catch (error) {
            if (error.errorCode && error.errorCode === "EntityAlreadyExists") {
                return this._model._modalUserFeedback.displayError(error);
            }
            this._logger.warn({error: error}, "OsPlanEditor.moveDevices() error");
        }
    }
    /* jshint ignore:end */

    /* ******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS   */
    /* START PUBLIC COMPONENT RENDERER METHODS  */
    /* ******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._setIcons(this._model._isDeleteDisabled, this._model._isUpdateDisabled);
        return Promise.resolve();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /* ******************************************/
    /* END COMPONENT RENDERER METHODS           */
    /* START HANDLER METHODS                    */
    /* ******************************************/

    _onLinks(newVal) {
        this._model.setValue(newVal);
        this._setIcons(this._model._isDeleteDisabled, this._model._isUpdateDisabled);
    }

    /* ******************************************/
    /* END HANDLER METHODS                      */
    /* START PRIVATE API METHODS                */
    /* ******************************************/

    _setIcons(deleteDisabled = true, updateDisabled = true) {
        this._rootElem.innerHTML = "";
        this._rootElem.appendChild(this._updateStatusIcon);
        this._rootElem.appendChild(this._planRemoveIcon);
        this._updateStatusIcon.setDisabled(updateDisabled);
        this._planRemoveIcon.setDisabled(deleteDisabled);
    }

    /* *****************************************
    /* END PRIVATE API METHODS
    /* ***************************************** */
}

module.exports = LinksRenderer;
